import {
  Box,
  Button,
  FormControl,
  IconButton,
  Modal,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Task } from "../../../../../services/phpServices";
import { CustomListItem } from "../../../components/ListItem";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { Moment } from "moment";
import moment from "moment";
import { Colors } from "../../../../../theme";
import { observer } from "mobx-react-lite";

interface EditTaskProps {
  open: boolean;
  onConfirmed?: (t: Task) => void;
  onClose?: () => void;
  taskData?: Task;
}

const EditTask: React.FC<EditTaskProps> = observer(
  ({ open, onConfirmed, onClose, taskData }) => {
    const defaultTask: Task = {
      task_id: 0,
      name: "",
      description: "",
      note: 0,
      photo: 0,
      photo_from_library: 0,
      checkin: 0,
      require_time: 0,
      status: "PUBLISH",
    };

    const [task, setTask] = useState<Task>(taskData || defaultTask);
    const { t } = useTranslation() as any;
    const [loading, setLoading] = useState(false);
    const [requiredTime, setRequiredTime] = useState<Moment | null>(
      moment().minute() > 0 ? moment().startOf("hour").add(1, "hour") : moment()
    );
    const [isOpenTimePicker, setIsOpenTimePicker] = useState(false);
    const [isFocusedName, setIsFocusedName] = useState(false);
    const [isFocusedDes, setIsFocusedDes] = useState(false);
    const [photoLibrary, setPhotoLibrary] = useState(false);

    const handleChange = (field: keyof Task, value: any) => {
      setTask((prev) => ({ ...prev, [field]: value }));
    };

    const handleClose = () => {
      setTask(defaultTask);
      if (onClose) onClose();
    };

    const handleSwitchChange = (field: keyof Task, checked: boolean) => {
      setTask((prevTask: any) => ({
        ...prevTask,
        [field]:
          field === "status"
            ? checked
              ? "PUBLISH"
              : "UNPUBLISH"
            : checked
            ? field === "require_time"
              ? requiredTime?.hours() * 60 + requiredTime?.minutes()
              : 1
            : 0,
      }));

      if (field === "require_time" && checked) {
        setRequiredTime(
          moment().minute() > 0
            ? moment().startOf("hour").add(1, "hour")
            : moment()
        );
        setIsOpenTimePicker(true);
      } else if (field === "require_time" && !checked) {
        setRequiredTime(null);
        setIsOpenTimePicker(false);
      }
    };

    const handleConfirm = async () => {
      setLoading(true);
      try {
        if (onConfirmed) {
          onConfirmed(task);
          handleClose();
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      if (open && taskData?.require_time && taskData.require_time > 0) {
        const hour = Math.floor(taskData.require_time / 60);
        const minute = taskData.require_time % 60;
        setRequiredTime(
          moment().startOf("day").add(hour, "hours").add(minute, "minutes")
        );
        setIsOpenTimePicker(true);
      } else {
        setIsOpenTimePicker(false);
        setRequiredTime(null);
      }

      if (open && taskData) {
        setTask(taskData);
      } else {
        setTask(defaultTask);
      }
    }, [open, taskData]);

    return (
      <Modal open={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            backgroundColor: "white",
            borderRadius: "4px",
            boxShadow: 24,
            width: "500px",
            maxHeight: "80vh",
            transform: "translate(-50%, -50%)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "40px",
              backgroundColor: "#08B0A0",
              display: "flex",
              alignItems: "center",
              borderRadius: "4px 4px 0 0",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="h5"
              color={Colors.white}
              sx={{ paddingLeft: "16px" }}
            >
              {t("00_03_edit").toUpperCase()}
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon sx={{ color: "white", width: 24, height: 24 }} />
            </IconButton>
          </Box>

          <Box
            sx={{
              paddingLeft: "20px",
              paddingRight: "20px",
              paddingBottom: "20px",
              overflowY: "auto",
              maxHeight: "calc(80vh - 70px)",
              marginTop: "8px",
            }}
          >
            <CustomListItem
              leftContainerWidth={150}
              text={`${t("26_00_name")} *`}
              containerStyle={{ marginBottom: "16px" }}
              RightComponent={
                <TextField
                  id="task-name"
                  label={t("26_00_name")}
                  variant="standard"
                  onFocus={() => setIsFocusedName(true)}
                  onBlur={(e) => {
                    if (!e.target.value) setIsFocusedName(false);
                  }}
                  style={{ width: "100%", fontSize: "13px" }}
                  value={task.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  inputProps={{ maxLength: 200 }}
                  InputProps={{
                    style: {
                      fontSize: "13px",
                      fontWeight: "400",
                    },
                  }}
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: "12px",
                      fontWeight: "400",
                      opacity: isFocusedName ? 0 : 1,
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "13px",
                      fontWeight: "400",
                    },
                  }}
                />
              }
            />

            <CustomListItem
              leftContainerWidth={150}
              text={t("26_00_description")}
              containerStyle={{ marginBottom: "16px" }}
              RightComponent={
                <TextField
                  id="description"
                  label={t("26_00_description")}
                  variant="standard"
                  style={{ width: "100%", fontSize: "13px" }}
                  value={task.description}
                  onFocus={() => setIsFocusedDes(true)}
                  onBlur={(e) => {
                    if (!e.target.value) setIsFocusedDes(false);
                  }}
                  inputProps={{ maxLength: 450 }}
                  onChange={(e) => handleChange("description", e.target.value)}
                  InputProps={{
                    style: {
                      fontSize: "13px",
                      fontWeight: "400",
                    },
                  }}
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: "12px",
                      fontWeight: "400",
                      opacity: isFocusedDes ? 0 : 1,
                    },
                    "& .MuiInputBase-input": {
                      fontSize: "13px",
                      fontWeight: "400",
                    },
                  }}
                />
              }
            />

            <CustomListItem
              leftContainerWidth={150}
              text={t("26_00_status")}
              containerStyle={{ marginTop: "20px", marginBottom: "80px" }}
              RightComponent={
                <Box display="flex" flexDirection="column" gap={2}>
                  <FormControl>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Switch
                        checked={task.note === 1}
                        onChange={(e) =>
                          handleSwitchChange("note", e.target.checked)
                        }
                      />
                      <Typography>{t("26_00_require_note")}</Typography>
                    </Box>
                  </FormControl>

                  <FormControl>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Switch
                        checked={task.photo === 1}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setPhotoLibrary(isChecked);
                          handleSwitchChange("photo", isChecked);
                          if (!isChecked)
                            handleSwitchChange("photo_from_library", false);
                        }}
                      />
                      <Typography>{t("26_00_require_photo")}</Typography>
                    </Box>
                  </FormControl>

                  {photoLibrary && (
                    <FormControl sx={{ ml: 3 }}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Switch
                          checked={task.photo_from_library === 1}
                          onChange={(e) =>
                            handleSwitchChange(
                              "photo_from_library",
                              e.target.checked
                            )
                          }
                        />
                        <Typography>{t("26_00_allow_upload_photo")}</Typography>
                      </Box>
                    </FormControl>
                  )}

                  <FormControl>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Switch
                        checked={task.require_time > 0}
                        onChange={(e) =>
                          handleSwitchChange("require_time", e.target.checked)
                        }
                      />
                      <Typography>{t("26_00_require_time")}</Typography>
                    </Box>
                  </FormControl>

                  {isOpenTimePicker && (
                    <FormControl sx={{ padding: "20px" }}>
                      <TimePicker
                        label="Select Time"
                        value={requiredTime}
                        minutesStep={15}
                        onChange={(newValue) => {
                          setRequiredTime(newValue);
                          handleChange(
                            "require_time",
                            newValue
                              ? newValue.hours() * 60 + newValue.minutes()
                              : 0
                          );
                        }}
                        slotProps={{
                          textField: {
                            variant: "standard",
                            InputProps: {
                              style: { fontSize: "13px", fontWeight: "400" },
                            },
                            InputLabelProps: {
                              style: { fontSize: "13px", fontWeight: "400" },
                            },
                          },
                          actionBar: {
                            sx: { display: "flex", justifyContent: "center" },
                          },
                        }}
                      />
                    </FormControl>
                  )}

                  <FormControl>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Switch
                        checked={task.status === "PUBLISH"}
                        onChange={(e) =>
                          handleSwitchChange("status", e.target.checked)
                        }
                      />
                      <Typography>{t("26_00_publish")}</Typography>
                    </Box>
                  </FormControl>
                </Box>
              }
            />

            <Box
              sx={{
                position: "absolute",
                bottom: "0",
                right: "0",
                display: "flex",
                gap: "20px",
                backgroundColor: "white",
                padding: "20px 20px",
                borderRadius: "4px",
                width: "100%",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={loading}
              >
                {t("00_03_cancel")}
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirm}
                disabled={task.name === "" || loading}
              >
                {t("00_03_save")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    );
  }
);

export default EditTask;
